import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { WorkOrder } from '../types';
import { UserProfile } from '../../../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { formatPhone, buildFullAddress, formatDateFull } from './receiptPdfUtils';

const sanitize = (str: string = ''): string => {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br/>')
    .replace(/"/g, '&quot;')
    .trim();
};

const sanitizeImageSource = (source: string = ''): string => {
  return String(source)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

export interface WorkOrderPdfResult {
  blob: Blob;
  fileName: string;
}

/**
 * Gera o documento PDF real de alta resolução para Ordem de Serviço
 */
export async function generateWorkOrderPdf(
  workOrder: Partial<WorkOrder>,
  user?: Partial<UserProfile>
): Promise<WorkOrderPdfResult> {
  const shadowContainer = document.createElement('div');
  shadowContainer.id = 'rmax-workorder-pdf-shadow';
  shadowContainer.style.position = 'fixed';
  shadowContainer.style.left = '-10000px';
  shadowContainer.style.top = '-10000px';
  shadowContainer.style.width = '800px';
  shadowContainer.style.backgroundColor = '#ffffff';
  shadowContainer.style.color = '#18181b';
  shadowContainer.style.fontFamily = 'system-ui, -apple-system, sans-serif';
  shadowContainer.style.padding = '36px 40px';
  shadowContainer.style.boxSizing = 'border-box';

  const orderNum = workOrder.orderNumber || 'OS-001';
  const clientName = workOrder.clientName || 'Cliente';
  const clientDoc = workOrder.clientDocument ? `CPF/CNPJ: ${sanitize(workOrder.clientDocument)}` : '';
  const clientPhone = workOrder.clientPhone ? `WhatsApp: ${formatPhone(workOrder.clientPhone)}` : '';
  const clientAddress = workOrder.clientAddress ? sanitize(workOrder.clientAddress) : '';
  const clientInfoLine = [clientDoc, clientPhone, clientAddress].filter(Boolean).join(' • ');

  const providerName = user?.businessName || user?.ownerName || 'Prestador Especializado';
  const providerDoc = user?.document ? `CNPJ/CPF: ${sanitize(user.document)}` : '';
  const providerPhone = user?.phone ? `WhatsApp: ${formatPhone(user.phone)}` : '';
  const providerAddress = buildFullAddress(user);
  const providerInfoLine = [providerDoc, providerPhone, providerAddress].filter(Boolean).join(' • ');
  const rawLogoUrl = user?.logoUrl || user?.logoBase64;
  const logoUrl = rawLogoUrl ? sanitizeImageSource(rawLogoUrl) : '';

  const items = workOrder.itemsBreakdown || [];
  const laborItems = items.filter(i => !i.isMaterial);
  const materialItems = items.filter(i => i.isMaterial);

  const checklists = workOrder.checklistData || [];
  const photos = (workOrder.photos || []).slice(0, 6);
  const signatures = workOrder.signatures || [];

  const totalLabor = workOrder.totalLabor || laborItems.reduce((acc, curr) => acc + curr.total, 0);
  const totalMaterials = workOrder.totalMaterials || materialItems.reduce((acc, curr) => acc + curr.total, 0);
  const finalPrice = workOrder.finalPrice || (totalLabor + totalMaterials);

  shadowContainer.innerHTML = `
    <div style="border: 2px solid #e4e4e7; border-radius: 18px; padding: 28px; background: #ffffff;">
      <!-- Header com Logo e Dados da Empresa -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #18181b; padding-bottom: 18px; margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 14px; max-width: 60%;">
          ${logoUrl ? `
            <img src="${logoUrl}" style="width: 56px; height: 56px; border-radius: 12px; object-fit: cover; border: 1px solid #e4e4e7;" crossOrigin="anonymous" />
          ` : `
            <div style="width: 56px; height: 56px; border-radius: 12px; background: #ecfdf5; border: 2px solid #059669; display: flex; align-items: center; justify-content: center; font-weight: 900; color: #059669; font-size: 16px;">
              OS
            </div>
          `}
          <div>
            <h1 style="margin: 0; font-size: 18px; font-weight: 900; color: #18181b; text-transform: uppercase;">
              ${sanitize(providerName)}
            </h1>
            ${providerInfoLine ? `
              <p style="margin: 4px 0 0 0; font-size: 10px; color: #71717a; line-height: 1.4;">
                ${providerInfoLine}
              </p>
            ` : ''}
          </div>
        </div>

        <div style="text-align: right;">
          <span style="display: inline-block; padding: 4px 10px; background: #059669; color: #ffffff; font-size: 11px; font-weight: 900; border-radius: 8px; text-transform: uppercase; letter-spacing: 0.5px;">
            ORDEM DE SERVIÇO
          </span>
          <div style="font-size: 16px; font-weight: 900; color: #18181b; margin-top: 6px;">
            ${sanitize(orderNum)}
          </div>
          <div style="font-size: 10px; color: #71717a; margin-top: 2px;">
            Data: ${formatDateFull(workOrder.createdAt)}
          </div>
        </div>
      </div>

      <!-- Dados do Cliente & Título do Serviço -->
      <div style="background: #fafafa; border: 1px solid #e4e4e7; border-radius: 12px; padding: 14px 16px; margin-bottom: 18px;">
        <div style="font-size: 9px; font-weight: 900; color: #a1a1aa; text-transform: uppercase; letter-spacing: 0.5px;">
          DADOS DO CLIENTE & LOCAL DA EXECUÇÃO
        </div>
        <div style="font-size: 14px; font-weight: 900; color: #18181b; margin-top: 2px;">
          ${sanitize(clientName)}
        </div>
        ${clientInfoLine ? `
          <div style="font-size: 10px; color: #52525b; margin-top: 3px;">
            ${clientInfoLine}
          </div>
        ` : ''}
        <div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed #e4e4e7; font-size: 12px; font-weight: 800; color: #059669;">
          Serviço: ${sanitize(workOrder.title || 'Prestação de Serviços Técnicos')}
        </div>
      </div>

      <!-- Metadados Técnicos: Aparelho / Celular -->
      ${workOrder.deviceData ? `
        <div style="background: #fafafa; border: 1px solid #e4e4e7; border-radius: 12px; padding: 12px 16px; margin-bottom: 18px; font-size: 11px;">
          <div style="font-size: 9px; font-weight: 900; color: #a1a1aa; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
            APARELHO / DISPOSITIVO
          </div>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="width: 25%; color: #71717a;">Marca:</td>
              <td style="width: 25%; font-weight: 800; color: #18181b;">${sanitize(workOrder.deviceData.brand || '-')}</td>
              <td style="width: 25%; color: #71717a;">Modelo:</td>
              <td style="width: 25%; font-weight: 800; color: #18181b;">${sanitize(workOrder.deviceData.model || '-')}</td>
            </tr>
            <tr>
              <td style="color: #71717a; padding-top: 4px;">IMEI/Serial:</td>
              <td style="font-weight: 800; color: #18181b; padding-top: 4px;">${sanitize(workOrder.deviceData.imeiOrSerial || '-')}</td>
              <td style="color: #71717a; padding-top: 4px;">Cor:</td>
              <td style="font-weight: 800; color: #18181b; padding-top: 4px;">${sanitize(workOrder.deviceData.color || '-')}</td>
            </tr>
            ${workOrder.deviceData.hasPasscode ? `
              <tr>
                <td style="color: #71717a; padding-top: 4px;">Acesso de teste:</td>
                <td colspan="3" style="font-weight: 800; color: #18181b; padding-top: 4px;">Fornecido, valor não armazenado</td>
              </tr>
            ` : ''}
          </table>
        </div>
      ` : ''}

      <!-- Tabela de Itens e Escopo -->
      ${items.length > 0 ? `
        <div style="margin-bottom: 18px;">
          <div style="font-size: 10px; font-weight: 900; color: #18181b; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 0.5px;">
            ESCOPO E COMPOSIÇÃO DE VALORES
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background: #f4f4f5; border-bottom: 1px solid #d4d4d8; text-align: left;">
                <th style="padding: 6px 10px; font-weight: 900; color: #52525b;">Item / Descrição</th>
                <th style="padding: 6px 10px; font-weight: 900; color: #52525b; text-align: center; width: 60px;">Qtd</th>
                <th style="padding: 6px 10px; font-weight: 900; color: #52525b; text-align: right; width: 90px;">Unitário</th>
                <th style="padding: 6px 10px; font-weight: 900; color: #52525b; text-align: right; width: 100px;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${items.map((item, idx) => `
                <tr style="border-bottom: 1px solid #f4f4f5; ${idx % 2 === 1 ? 'background: #fafafa;' : ''}">
                  <td style="padding: 6px 10px; color: #18181b; font-weight: 700;">
                    ${sanitize(item.name)}
                    <span style="font-size: 9px; color: #71717a; font-weight: 500; margin-left: 4px;">
                      (${item.isMaterial ? 'Insumo/Peça' : 'Mão de Obra'})
                    </span>
                  </td>
                  <td style="padding: 6px 10px; text-align: center; color: #52525b;">${item.quantity || 1}</td>
                  <td style="padding: 6px 10px; text-align: right; color: #52525b;">${formatCurrencyBRL(item.unitPrice || 0)}</td>
                  <td style="padding: 6px 10px; text-align: right; color: #18181b; font-weight: 800;">${formatCurrencyBRL(item.total || 0)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      <!-- Resumo Financeiro da OS -->
      <div style="display: flex; justify-content: flex-end; margin-bottom: 18px;">
        <div style="width: 280px; background: #fafafa; border: 1px solid #e4e4e7; border-radius: 10px; padding: 10px 14px; font-size: 11px;">
          ${totalMaterials > 0 ? `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #71717a;">
              <span>Materiais & Peças:</span>
              <strong style="color: #18181b;">${formatCurrencyBRL(totalMaterials)}</strong>
            </div>
          ` : ''}
          ${totalLabor > 0 ? `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #71717a;">
              <span>Mão de Obra Especializada:</span>
              <strong style="color: #18181b;">${formatCurrencyBRL(totalLabor)}</strong>
            </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; border-top: 2px solid #18181b; padding-top: 6px; margin-top: 4px; font-size: 13px;">
            <span style="font-weight: 900; color: #18181b;">VALOR TOTAL DA OS:</span>
            <strong style="font-weight: 900; color: #059669;">${formatCurrencyBRL(finalPrice)}</strong>
          </div>
        </div>
      </div>

      <!-- Checklist Técnico de Vistoria -->
      ${checklists.length > 0 ? `
        <div style="margin-bottom: 16px; background: #ffffff; border: 1px solid #e4e4e7; border-radius: 10px; padding: 10px 14px;">
          <div style="font-size: 10px; font-weight: 900; color: #18181b; text-transform: uppercase; margin-bottom: 6px;">
            CHECKLIST TÉCNICO DE VISTORIA & CONFORMIDADE
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 10px; color: #3f3f46;">
            ${checklists.map(c => `
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="color: #059669; font-weight: 900;">✓</span>
                <span>${sanitize(typeof c === 'string' ? c : c.label)}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Fotos da Vistoria (se houver) -->
      ${photos.length > 0 ? `
        <div style="margin-bottom: 16px;">
          <div style="font-size: 10px; font-weight: 900; color: #18181b; text-transform: uppercase; margin-bottom: 6px;">
            REGISTRO FOTOGRÁFICO DE VISTORIA (${photos.length} FOTOS)
          </div>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            ${photos.map(p => `
              <div style="width: 105px; border: 1px solid #e4e4e7; border-radius: 8px; overflow: hidden; background: #fafafa;">
                <img src="${sanitizeImageSource(p.photoUrl)}" style="width: 100%; height: 75px; object-fit: cover;" crossOrigin="anonymous" />
                <div style="padding: 2px 4px; font-size: 8px; font-weight: 800; text-align: center; color: #52525b; text-transform: uppercase;">
                  ${p.photoType === 'before' ? 'Antes' : p.photoType === 'during' ? 'Durante' : 'Depois'}
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Garantia e Condições de Pagamento -->
      <div style="border-top: 1px solid #e4e4e7; padding-top: 12px; margin-bottom: 16px; font-size: 10px; color: #52525b; line-height: 1.4;">
        ${workOrder.warrantyTerms ? `
          <div style="margin-bottom: 4px;">
            <strong style="color: #18181b;">Garantia Legal:</strong> ${sanitize(workOrder.warrantyTerms)}
          </div>
        ` : ''}
        ${workOrder.paymentTerms ? `
          <div>
            <strong style="color: #18181b;">Condições de Pagamento:</strong> ${sanitize(workOrder.paymentTerms)}
          </div>
        ` : ''}
      </div>

      <!-- Assinaturas Digitais Coletadas -->
      ${signatures.length > 0 ? `
        <div style="border-top: 2px dashed #d4d4d8; padding-top: 14px; display: flex; justify-content: space-around; text-align: center;">
          ${signatures.map(sig => `
            <div style="max-width: 260px;">
              <img src="${sig.clientSignatureBase64 || sig.providerSignatureBase64 || ''}" style="height: 42px; object-fit: contain; margin: 0 auto 4px auto;" />
              <div style="border-top: 1px solid #18181b; padding-top: 4px; font-size: 10px; font-weight: 900; color: #18181b;">
                ${sanitize(sig.clientName || sig.providerName || 'Assinante')} (${sig.clientSignatureBase64 ? 'Cliente' : 'Prestador Responsável'})
              </div>
              <div style="font-size: 8px; color: #71717a; font-family: monospace; margin-top: 2px;">
                Hash: ${sig.verificationHash?.slice(0, 16) || 'AUTÊNTICO'} • ${formatDateFull(sig.signedAt)}
              </div>
            </div>
          `).join('')}
        </div>
      ` : `
        <div style="border-top: 1px solid #e4e4e7; padding-top: 18px; display: flex; justify-content: space-around; text-align: center; font-size: 10px; color: #71717a;">
          <div style="width: 220px; border-top: 1px solid #71717a; padding-top: 4px;">
            Assinatura do Prestador
          </div>
          <div style="width: 220px; border-top: 1px solid #71717a; padding-top: 4px;">
            Assinatura / Aceite do Cliente
          </div>
        </div>
      `}
    </div>
  `;

  document.body.appendChild(shadowContainer);

  try {
    await new Promise(resolve => setTimeout(resolve, 150)); // Timeout mínimo exigido pela Constituição (Art 6)

    const canvas = await html2canvas(shadowContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: 800
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const printWidth = pageWidth - (margin * 2);
    const pageContentHeight = pageHeight - (margin * 2);
    const pixelsPerMm = canvas.width / printWidth;
    const pageContentHeightPx = Math.floor(pageContentHeight * pixelsPerMm);

    for (let pageOffsetPx = 0; pageOffsetPx < canvas.height; pageOffsetPx += pageContentHeightPx) {
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pageContentHeightPx, canvas.height - pageOffsetPx);
      const pageContext = pageCanvas.getContext('2d');
      if (!pageContext) throw new Error('Não foi possível preparar a página do PDF.');

      pageContext.drawImage(
        canvas,
        0,
        pageOffsetPx,
        canvas.width,
        pageCanvas.height,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height
      );

      if (pageOffsetPx > 0) pdf.addPage();
      const pageImage = pageCanvas.toDataURL('image/jpeg', 0.95);
      const pageImageHeight = pageCanvas.height / pixelsPerMm;
      pdf.addImage(pageImage, 'JPEG', margin, margin, printWidth, pageImageHeight);
    }
    const pdfBlob = pdf.output('blob');
    const safeOrder = (workOrder.orderNumber || 'OS').replace(/[^a-zA-Z0-9_-]/g, '_');
    const safeClient = (workOrder.clientName || 'Cliente').replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `Ordem_de_Servico_${safeOrder}_${safeClient}.pdf`;

    return { blob: pdfBlob, fileName };
  } finally {
    if (document.body.contains(shadowContainer)) {
      document.body.removeChild(shadowContainer);
    }
  }
}
